import React from "react";
import fp from "lodash/fp";
import moment from "moment";

import {
	agentPermissionSet,
	hasPermissions,
	merchantPermissionSet,
	uwDecisionPermissionSet,
	viewAppPermissionSet,
} from "helpers";
import { getAppStatuses } from "mappers";
import { ReviewTypeEnum } from "types";

import Mid from "../components/merchantNavInfo/mid";

import ApplicableFalse from "static/images/applicable_false.svg";
import ApplicableTrue from "static/images/applicable_true.svg";
import HighRiskFalse from "static/images/high_risk.svg";
import HighRiskTrue from "static/images/high_risk_true.svg";

export const getHighRiskIcon = (isHighRisk: boolean) =>
	isHighRisk ? HighRiskTrue : HighRiskFalse;

export const getApplicableIcon = (isApplicable: boolean) =>
	isApplicable ? ApplicableTrue : ApplicableFalse;

export const defineAgentOfficePackageClassName = (type: number) =>
	({ 1: "bronze", 2: "silver", 3: "gold" }[type] ?? "none");

export const getTabNameByPath = (tabs, path) => tabs[path];

export const getNavigationInfo = data => {
	const navInformation = [
		{
			label: "App ID",
			data: data?.applicationNumber,
			path:
				data?.applicationId && hasPermissions(viewAppPermissionSet)
					? `/applications/${data?.applicationId}/get-started`
					: "",
		},
		{
			label: "MID",
			data: data ? <Mid merchant={data} /> : "",
			path:
				data?.merchantId &&
				hasPermissions([
					...merchantPermissionSet,
					...uwDecisionPermissionSet,
				])
					? `/merchant/list/info/${data?.merchantId}/general`
					: "",
		},
		{
			label: "Business Name",
			data: data?.dbaName,
		},
		{
			label: "Application Status",
			data: getAppStatuses(
				data?.applicationStatus || data?.application_status,
			),
		},
		{
			label: "Merchant Status",
			data:
				typeof data?.merchantStatus === "string"
					? data?.merchantStatus
					: getAppStatuses(data?.merchantStatus),
		},
		{
			label: "Agent Office",
			data: data?.agentOffice,
			path:
				data?.agentOfficeId && hasPermissions(agentPermissionSet)
					? `/system-setup/agent-office-setup/edit-agent-office/${data?.agentOfficeId}/general-info`
					: "",
		},
		{
			label: "Agent Name",
			data: data?.agentName,
			path:
				data?.agentId && hasPermissions(agentPermissionSet)
					? `/system-setup/agent-setup/edit-agent/${data?.agentId}/general-info`
					: "",
		},
	];

	return navInformation;
};

export const getNavigationInfoHeaderDetails = data => {
	const mappedData = [
		{
			label: "MID",
			data: data ? <Mid merchant={data} /> : "",
			path:
				data?.merchantId &&
				hasPermissions([
					...merchantPermissionSet,
					...uwDecisionPermissionSet,
				])
					? `/merchant/list/info/${data?.merchantId}/general`
					: "",
		},

		{
			label: "Doing Business As Name (DBA)",
			data: data?.dbaName,
		},
	];

	return mappedData;
};

export const getNavigationInfoHeaderDetailsChargebacks = data => {
	const mappedData = [
		{
			label: "MID",
			data: data ? <Mid merchant={data} /> : "",
			path:
				data?.merchantId &&
				hasPermissions([
					...merchantPermissionSet,
					...uwDecisionPermissionSet,
				])
					? `/merchant/list/info/${data?.merchantId}/general`
					: "",
		},

		{
			label: "Doing Business As Name (DBA)",
			data: data?.dbaName,
		},

		{
			label: "Review Type",
			data: ReviewTypeEnum.getByName(data?.reviewType)?.displayName,
		},
	];

	return mappedData;
};

const mapFilterDate = value => {
	return value instanceof Array ? parseInt(value?.[0]) : parseInt(value);
};

export const ageMinMapper =
	({ maxDaysName, maxHoursName, maxMinutesName }) =>
	filterModel => {
		const daysValue = fp.get(maxDaysName, filterModel);
		const hoursValue = fp.get(maxHoursName, filterModel);
		const minutesValue = fp.get(maxMinutesName, filterModel);

		if (daysValue || hoursValue || minutesValue) {
			const subtract = {
				days: mapFilterDate(daysValue),
				hours: mapFilterDate(hoursValue),
				minutes: mapFilterDate(minutesValue),
			};

			return moment(Date.now()).subtract(subtract).format();
		} else {
			return undefined;
		}
	};

export const ageMaxMapper =
	({ minDaysName, minHoursName, minMinutesName }) =>
	filterModel => {
		const daysValue = fp.get(minDaysName, filterModel);
		const hoursValue = fp.get(minHoursName, filterModel);
		const minutesValue = fp.get(minMinutesName, filterModel);

		if (daysValue || hoursValue || minutesValue) {
			const subtract = {
				days: mapFilterDate(daysValue),
				hours: mapFilterDate(hoursValue),
				minutes: mapFilterDate(minutesValue),
			};

			return moment(Date.now()).subtract(subtract).format();
		} else {
			return undefined;
		}
	};

export const ageMinimum = ageMinMapper({
	maxDaysName: "ageMaxDay",
	maxHoursName: "ageMaxHours",
	maxMinutesName: "ageMaxMinutes",
});

export const ageMaximum = ageMaxMapper({
	minDaysName: "ageMinDay",
	minHoursName: "ageMinHours",
	minMinutesName: "ageMinMinutes",
});

export const ageMinApproved = ageMinMapper({
	maxDaysName: "age_in_approved.max_days",
	maxHoursName: "age_in_approved.max_hours",
	maxMinutesName: "age_in_approved.max_minutes",
});

export const ageMaxApproved = ageMaxMapper({
	minDaysName: "age_in_approved.min_days",
	minHoursName: "age_in_approved.min_hours",
	minMinutesName: "age_in_approved.min_minutes",
});

export const ageMinPend = ageMinMapper({
	maxDaysName: "age_of_pend.max_days",
	maxHoursName: "age_of_pend.max_hours",
	maxMinutesName: "age_of_pend.max_minutes",
});

export const ageMaxPend = ageMaxMapper({
	minDaysName: "age_of_pend.min_days",
	minHoursName: "age_of_pend.min_hours",
	minMinutesName: "age_of_pend.min_minutes",
});

export const ageMinUnderwriting = ageMinMapper({
	maxDaysName: "age_in_underwriting.max_days",
	maxHoursName: "age_in_underwriting.max_hours",
	maxMinutesName: "age_in_underwriting.max_minutes",
});

export const ageMaxUnderwriting = ageMaxMapper({
	minDaysName: "age_in_underwriting.min_days",
	minHoursName: "age_in_underwriting.min_hours",
	minMinutesName: "age_in_underwriting.min_minutes",
});
